import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzResultModule } from 'ng-zorro-antd/result';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { EhrService } from '@beautyapp/core';

@Component({
  selector: 'beautyapp-ehr-sms-verification',
  imports: [
    FormsModule,
    NzButtonModule,
    NzIconModule,
    NzInputModule,
    NzResultModule,
    NzAlertModule,
  ],
  template: `
    <div class="space-y-4">
      <!-- State: Already verified -->
      @if (alreadyVerified()) {
        <nz-result nzStatus="success" nzTitle="სმს ვერიფიკაცია დასრულებულია">
          <div nz-result-extra>
            <p class="text-gray-600 text-sm">დადასტურებული კოდი: <strong>{{ savedCode() }}</strong></p>
          </div>
        </nz-result>
      }

      <!-- State: Not sent yet -->
      @if (!alreadyVerified() && !codeSent()) {
        <p class="text-gray-700">სმს კოდი გაიგზავნება პაციენტის ტელეფონზე.</p>
        <button
          nz-button
          nzType="primary"
          [nzLoading]="sending()"
          (click)="sendCode()"
          class="w-full"
        >
          <span nz-icon nzType="message" nzTheme="outline"></span>
          კოდის გაგზავნა
        </button>
      }

      <!-- State: Code sent, awaiting verification -->
      @if (!alreadyVerified() && codeSent() && !verified()) {
        <p class="text-gray-700 text-sm">კოდი გაიგზავნა ნომერზე: <strong>{{ maskedPhone() }}</strong></p>
        <nz-input-group nzSize="large" [nzPrefix]="prefixIcon">
          <input
            nz-input
            [(ngModel)]="smsCode"
            placeholder="6-ციფრიანი კოდი"
            maxlength="6"
            (keyup.enter)="verify()"
          />
        </nz-input-group>
        <ng-template #prefixIcon>
          <span nz-icon nzType="lock" nzTheme="outline"></span>
        </ng-template>

        @if (verifyError()) {
          <nz-alert nzType="error" [nzMessage]="verifyError()!" nzShowIcon></nz-alert>
        }

        <div class="flex gap-2">
          <button
            nz-button
            nzType="primary"
            [nzLoading]="verifying()"
            [disabled]="!smsCode || smsCode.length < 6"
            (click)="verify()"
            class="flex-1"
          >
            დადასტურება
          </button>
          <button nz-button [nzLoading]="sending()" (click)="sendCode()">
            <span nz-icon nzType="reload" nzTheme="outline"></span>
            ხელახლა
          </button>
        </div>
      }

      <!-- State: Just verified -->
      @if (!alreadyVerified() && verified()) {
        <nz-result nzStatus="success" nzTitle="ვერიფიკაცია წარმატებით დასრულდა"></nz-result>
      }
    </div>
  `,
})
export class EhrSmsVerificationComponent {
  private data = inject(NZ_MODAL_DATA);
  private modal = inject(NzModalRef);
  private ehrService = inject(EhrService);
  private messageService = inject(NzMessageService);

  smsCode = '';
  sending = signal(false);
  verifying = signal(false);
  codeSent = signal(false);
  verified = signal(false);
  maskedPhone = signal('');
  verifyError = signal<string | null>(null);
  alreadyVerified = signal(false);
  savedCode = signal('');

  constructor() {
    if (this.data.consentSmsVerified) {
      this.alreadyVerified.set(true);
      this.savedCode.set(this.data.consentSmsCode || '');
    }
  }

  sendCode() {
    const ehrId = this.data.ehrId;
    const userId = this.data.userId;
    if (!ehrId || !userId) {
      this.messageService.error('პაციენტის მონაცემები არ არის ხელმისაწვდომი');
      return;
    }

    this.sending.set(true);
    this.verifyError.set(null);
    this.ehrService.sendSmsVerification(ehrId, userId).subscribe({
      next: (res: any) => {
        this.sending.set(false);
        this.codeSent.set(true);
        this.maskedPhone.set(res?.maskedPhone || '');
        this.smsCode = '';
        this.messageService.success('კოდი გაიგზავნა');
      },
      error: (err: any) => {
        this.sending.set(false);
        const msg = err?.error?.message || 'კოდის გაგზავნა ვერ მოხერხდა';
        this.messageService.error(msg);
      },
    });
  }

  verify() {
    const ehrId = this.data.ehrId;
    if (!ehrId || !this.smsCode) return;

    this.verifying.set(true);
    this.verifyError.set(null);
    this.ehrService.verifySmsCode(ehrId, this.smsCode).subscribe({
      next: (res: any) => {
        this.verifying.set(false);
        if (res?.verified) {
          this.verified.set(true);
          this.messageService.success('ვერიფიკაცია წარმატებით დასრულდა');
        } else {
          this.verifyError.set('არასწორი კოდი. სცადეთ თავიდან.');
        }
      },
      error: () => {
        this.verifying.set(false);
        this.verifyError.set('დადასტურება ვერ მოხერხდა');
      },
    });
  }
}
